import { getPlayerImageUrl, getTeamImageUrl } from '../constants/config';
import { extractEntity, extractList, toDateInputValue } from './tournamentsApi';

export const PLAYER_POSITIONS = ['GK', 'CB', 'LB', 'RB', 'CM', 'LM', 'RM', 'ST'];
export const PLAYERS_PAGE_SIZE = 40;

export const emptyPlayerForm = {
  family: '',
  name: '',
  father_name: '',
  position: 'CM',
  gender: '1',
  isProfessional: '0',
  isInsured: false,
  insuranceUntil: '',
  birthday: '',
  height: '',
  weight: '',
  nationality: '',
  application_id: '',
  number: '',
};

export const getPlayerPhotoUrl = (image) => getPlayerImageUrl(image);
export const getTeamPhotoUrl = (image) => getTeamImageUrl(image);

export const playerCardPath = (playerId, teamId) => (
  teamId
    ? `/teams_management/${teamId}/player/${playerId}`
    : `/players_management/${playerId}`
);

export const playerNewPath = (teamId) => (
  teamId ? `/teams_management/${teamId}/player/new` : '/players_management/new'
);

export const playerAppNewPath = (playerId, teamId) => (
  teamId
    ? `/teams_management/${teamId}/player/${playerId}/application/new`
    : `/players_management/${playerId}/application/new`
);

export const playerAppChangePath = (playerId, appTeamId, applicationId, teamId) => (
  teamId
    ? `/teams_management/${teamId}/player/${playerId}/application/${appTeamId}/${applicationId}`
    : `/players_management/${playerId}/application/${appTeamId}/${applicationId}`
);

export const playerShirtNumber = (value) => {
  const number = Number(value);
  if (value === '' || value == null || !Number.isFinite(number) || number <= 0) {
    return null;
  }
  return number;
};

export const formatPlayerNumber = (value) => {
  const number = playerShirtNumber(value);
  return number == null ? '—' : String(number);
};

export const isPlayerCaptain = (item, player) => {
  if (Number(item?.capitan) === 1 || Number(item?.isCaptain) === 1) return true;
  const teamId = Number(item?.team_id);
  if (!teamId) return false;
  return extractList(player?.related_teams).some((row) => (
    Number(row.team_id) === teamId && Number(row.capitan) === 1
  ));
};

export const isPlayerProfessional = (item) => {
  if (!item || typeof item !== 'object') return false;
  const value = item.isProfessional ?? item.is_professional;
  return value === true || value === 1 || value === '1';
};

export const isPlayerInsured = (item) => {
  if (!item || typeof item !== 'object') return false;
  const value = item.isInsured ?? item.is_insured;
  return value === true || value === 1 || value === '1';
};

export const playerDisplayName = (item) => (
  [item?.family, item?.name, item?.father_name].filter(Boolean).join(' ').trim()
);

export const playerFromEntity = (player) => ({
  family: player?.family || '',
  name: player?.name || '',
  father_name: player?.father_name || '',
  position: player?.position || 'CM',
  gender: player?.gender != null ? String(Number(player.gender) === 1 ? 1 : 0) : '1',
  isProfessional: player?.isProfessional != null
    ? String(Number(player.isProfessional) === 1 ? 1 : 0)
    : '0',
  isInsured: isPlayerInsured(player),
  insuranceUntil: toDateInputValue(player?.insuranceUntil ?? player?.insurance_until),
  birthday: toDateInputValue(player?.birthday),
  height: player?.height != null && player?.height !== '' ? String(player.height) : '',
  weight: player?.weight != null && player?.weight !== '' ? String(player.weight) : '',
  nationality: player?.nationality || '',
});

export const buildPlayerPayload = (form, { imageBase64, champId, playerId, isNew, teamId } = {}) => {
  const insured = form.isInsured === true || form.isInsured === 1 || form.isInsured === '1';
  const insuranceUntil = insured
    ? (toDateInputValue(form.insuranceUntil) || null)
    : null;
  const payload = {
    family: form.family.trim(),
    name: form.name.trim(),
    father_name: form.father_name.trim() || null,
    gender: Number(form.gender) === 1 ? 1 : 0,
    isProfessional: Number(form.isProfessional) === 1 ? 1 : 0,
    isInsured: insured ? 1 : 0,
    insuranceUntil,
    birthday: form.birthday || null,
    position: form.position || null,
    nationality: form.nationality.trim() || null,
  };

  const height = Number(form.height);
  const weight = Number(form.weight);
  payload.height = Number.isFinite(height) && height > 0 ? height : null;
  payload.weight = Number.isFinite(weight) && weight > 0 ? weight : null;

  if (champId) {
    payload.championat_id = Number(champId);
    payload.champ_id = Number(champId);
  }

  if (imageBase64) {
    payload.image = imageBase64;
  }

  if (!isNew && playerId) {
    payload.id = Number(playerId);
    payload.player_id = Number(playerId);
  }

  if (isNew && teamId) {
    payload.team_id = Number(teamId);
    if (form.application_id) {
      payload.application_id = Number(form.application_id);
    }
    const number = playerShirtNumber(form.number);
    if (number != null) {
      payload.number = number;
    }
  }

  return payload;
};

export const extractPlayer = (data) => {
  const entity = extractEntity(data);
  return {
    ...entity,
    id: entity.id || entity.player_id,
    teams: extractList(entity?.teams),
    related_profiles: extractList(entity?.related_profiles),
    possible_profiles: extractList(entity?.possible_profiles),
    transfers: extractList(entity?.transfers),
    related_teams: extractList(entity?.related_teams),
  };
};

export const extractPlayersPage = (data) => {
  if (Array.isArray(data)) {
    return { items: data, total: null };
  }
  const items = Array.isArray(data?.players) ? data.players : extractList(data);
  const totalRaw = data?.total ?? data?.count ?? data?.totalCount ?? data?.total_count;
  const total = Number(totalRaw);
  return {
    items,
    total: Number.isFinite(total) && total >= 0 ? total : null,
  };
};

export const uniquePlayerTeams = (teams) => {
  const seen = new Set();
  return extractList(teams).filter((team) => {
    const key = Number(team?.team_id || team?.id) || `name:${team?.team_name || team?.name || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const playerPositionLabel = (player) => (
  player?.position_name || player?.short_position || player?.position || ''
);
